import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CalendarDays, MapPin, Moon, DollarSign, Users, CheckCircle2, X, Award, Hourglass, Inbox, MessageCircle, Star, Camera } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending_approval: { label: "Nouvelle demande", className: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Acceptée · attente paiement", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Refusée", className: "bg-red-50 text-red-700 border-red-200" },
  expired: { label: "Expirée", className: "bg-zinc-100 text-zinc-500 border-zinc-200" },
  pending: { label: "Paiement en cours", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  confirmed: { label: "Confirmée", className: "bg-green-50 text-green-700 border-green-200" },
  cancelled: { label: "Annulée", className: "bg-red-50 text-red-700 border-red-200" },
  completed: { label: "Terminée", className: "bg-blue-50 text-blue-700 border-blue-200" },
};

function formatDate(d) { try { return format(new Date(d), "d MMM yyyy", { locale: fr }); } catch { return d; } }
function getPhotoUrl(photo) {
  if (!photo) return null;
  if (photo.startsWith("/api/")) return `${BACKEND_URL}${photo}`;
  return photo;
}

function RequestCard({ booking, index, onApprove, onReject, processing }) {
  const si = STATUS_BADGE[booking.status] || STATUS_BADGE.pending;
  const initial = booking.other_user_name?.charAt(0)?.toUpperCase() || "?";
  const photoUrl = getPhotoUrl(booking.other_user_photo);
  const isPending = booking.status === "pending_approval";

  return (
    <Card className={`border rounded-2xl bg-white ${isPending ? "border-amber-200 shadow-sm" : "border-zinc-200"}`} data-testid={`request-card-${index}`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-bold text-zinc-950 truncate">{booking.space_title}</h3>
            <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {booking.space_address || ""}</p>
          </div>
          <Badge className={`${si.className} border text-xs flex-shrink-0`}>{si.label}</Badge>
        </div>

        {/* Campeur profile */}
        <Link to={`/users/${booking.other_user_id}`} className="block group" data-testid={`request-user-${index}`}>
          <div className="flex items-start gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100 group-hover:border-emerald-300 group-hover:bg-emerald-50/30 transition-colors">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {photoUrl ? <img src={photoUrl} alt={booking.other_user_name} className="w-full h-full object-cover" /> : <span className="text-base font-bold text-emerald-700">{initial}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-zinc-900 text-sm truncate">{booking.other_user_name}</p>
                {booking.other_user_rating_count > 0 ? (
                  <div className="flex items-center gap-1 text-xs text-zinc-600">
                    <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                    <span className="font-medium">{booking.other_user_avg_rating}/5</span>
                    <span className="text-zinc-400">({booking.other_user_rating_count})</span>
                  </div>
                ) : (
                  <span className="text-xs text-zinc-400">Nouveau</span>
                )}
                {booking.other_user_is_superstar && (
                  <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 text-[10px] py-0 px-1.5 h-4"><Award className="h-2.5 w-2.5" /> Superstar</Badge>
                )}
              </div>
              {booking.other_user_bio && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{booking.other_user_bio}</p>}
              <p className="text-xs text-emerald-700 mt-1.5 font-medium opacity-0 group-hover:opacity-100 transition-opacity">Voir le profil et les avis &rarr;</p>
            </div>
          </div>
        </Link>

        {/* Message */}
        {booking.request_message && (
          <div className="text-sm text-zinc-700 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100 flex gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="italic">"{booking.request_message}"</p>
          </div>
        )}

        {/* Détails */}
        <div className="grid grid-cols-2 gap-3 text-sm text-zinc-600">
          <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-zinc-400" /><span>{formatDate(booking.check_in)} - {formatDate(booking.check_out)}</span></div>
          <div className="flex items-center gap-2"><Moon className="h-4 w-4 text-zinc-400" /><span>{booking.total_nights} nuit{booking.total_nights > 1 ? "s" : ""}</span></div>
          {booking.num_spots > 1 && <div className="flex items-center gap-2"><Users className="h-4 w-4 text-zinc-400" /><span>{booking.num_spots} emplacement(s)</span></div>}
          <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-zinc-400" /><span className="font-bold text-zinc-950">{(booking.host_payout ?? (booking.total_price > (booking.base_price || 0) ? booking.base_price : (booking.base_price || booking.total_price || 0) - (booking.platform_fee || 0)))?.toFixed(2)} $</span><span className="text-xs text-zinc-500 font-normal">(vous recevez)</span></div>
        </div>
        <p className="text-xs text-zinc-400 -mt-1">Le campeur paie {booking.total_price?.toFixed(2)} $. Frais de service de {booking.platform_fee?.toFixed(2)} $ deduits de votre paiement.</p>

        {isPending && booking.expires_at && (
          <p className="text-xs text-amber-700 flex items-center gap-1"><Hourglass className="h-3 w-3" /> Réponse attendue avant {formatDate(booking.expires_at)}</p>
        )}
        {booking.status === "rejected" && booking.reject_reason && (
          <p className="text-xs text-red-600">Motif refus : {booking.reject_reason}</p>
        )}

        {/* Checkout report from camper */}
        {booking.checkout_report && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid={`request-checkout-report-${index}`}>
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800">
              <Camera className="h-3.5 w-3.5" /> Rapport de départ - {booking.checkout_report.renter_name}
            </div>
            {booking.checkout_report.photo_url && (
              <img
                src={booking.checkout_report.photo_url.startsWith("/api/") ? `${BACKEND_URL}${booking.checkout_report.photo_url}` : booking.checkout_report.photo_url}
                alt="Etat du terrain au départ"
                className="w-full max-h-56 object-cover rounded-lg"
              />
            )}
            {booking.checkout_report.comment && <p className="text-sm text-zinc-700 italic">"{booking.checkout_report.comment}"</p>}
          </div>
        )}

        {isPending && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 flex-1 sm:flex-initial" onClick={() => onApprove(booking)} disabled={processing} data-testid={`approve-request-${index}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accepter
            </Button>
            <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 flex-1 sm:flex-initial" onClick={() => onReject(booking)} data-testid={`reject-request-${index}`}>
              <X className="h-3.5 w-3.5 mr-1" /> Refuser
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function RequestsPage() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectBooking, setRejectBooking] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const load = () => api.get("/bookings/received")
    .then(r => setBookings(r.data))
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const approve = async (b) => {
    setProcessing(true);
    try { await api.put(`/bookings/${b.id}/approve`); toast.success("Demande acceptée!"); load(); }
    catch (err) {
      const detail = err.response?.data?.detail || "Erreur";
      if (err.response?.status === 403 && /Stripe/i.test(detail)) {
        toast.error(detail, {
          duration: 8000,
          action: { label: "Configurer Stripe", onClick: () => { window.location.href = "/profile"; } },
        });
      } else {
        toast.error(detail);
      }
    }
    finally { setProcessing(false); }
  };

  const submitReject = async () => {
    if (!rejectBooking) return;
    setProcessing(true);
    try {
      await api.put(`/bookings/${rejectBooking.id}/reject`, { reason: rejectReason });
      toast.success("Demande refusée");
      setRejectBooking(null); setRejectReason("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setProcessing(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;

  const pending = bookings.filter(b => b.status === "pending_approval");
  const processed = bookings.filter(b => b.status !== "pending_approval");

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center"><Inbox className="h-5 w-5 text-amber-700" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" data-testid="requests-title">Demandes de réservation</h1>
          <p className="text-sm text-zinc-500">{pending.length} demande{pending.length > 1 ? "s" : ""} en attente</p>
        </div>
      </div>

      <Tabs defaultValue="pending" data-testid="requests-tabs">
        <TabsList className="mb-6 bg-zinc-100 rounded-xl p-1">
          <TabsTrigger value="pending" className="rounded-lg gap-2" data-testid="tab-pending">
            En attente ({pending.length})
            {pending.length > 0 && <Badge className="bg-amber-500 text-white border-0 text-[10px] h-4 px-1.5">{pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="processed" className="rounded-lg" data-testid="tab-processed">Historique ({processed.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {pending.length === 0 ? (
            <div className="text-center py-16" data-testid="no-pending-requests">
              <Inbox className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
              <p className="text-zinc-500">Aucune demande en attente</p>
              <p className="text-xs text-zinc-400 mt-2">Les nouvelles demandes apparaitront ici</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pending.map((b, i) => <RequestCard key={b.id} booking={b} index={i} onApprove={approve} onReject={setRejectBooking} processing={processing} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="processed">
          {processed.length === 0 ? (
            <div className="text-center py-16" data-testid="no-processed-requests">
              <p className="text-zinc-400 text-sm">Aucune demande traitee pour le moment</p>
            </div>
          ) : (
            <div className="space-y-4">
              {processed.map((b, i) => <RequestCard key={b.id} booking={b} index={i} onApprove={approve} onReject={setRejectBooking} processing={processing} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectBooking} onOpenChange={(v) => { if (!v) { setRejectBooking(null); setRejectReason(""); } }}>
        <DialogContent className="rounded-2xl" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Refuser la demande</DialogTitle>
            <DialogDescription>Indiquez une raison (optionnel). Le campeur sera notifié.</DialogDescription>
          </DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ex: Dates indisponibles..." className="rounded-xl min-h-[80px]" maxLength={300} data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setRejectBooking(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700 rounded-xl" onClick={submitReject} disabled={processing} data-testid="reject-confirm-btn">{processing ? "Envoi..." : "Refuser"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
